import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { TradeService } from './trade.service';
import {
  AlertService,
  BLOCK_EXPLORER_TX_LINK,
  ChainRepository,
  EthErrorUtil,
  IsLoadingService,
  SignerService,
  Trade,
  TradeSateEnum,
  Transaction,
  TransactionInput,
  TransactionType,
  WalletModalService,
} from 'common';
import {
  RebalancingRepository,
  TransactionExecutionRepository,
} from '../repository';
import { APP_CONFIG } from 'app-config';
import { Rebalancing } from '../model';
import { TransactionService } from './transaction.service';
import { RebalancingStep } from '../component';
import {
  Address,
  BatchTrade__factory,
  ERC20__factory,
  sleep,
} from '@31third/common';
import { TransactionExecutionStep } from '../enum';
import { SentryService } from './sentry.service';
import SafeAppsSDK, { TransactionStatus } from '@safe-global/safe-apps-sdk';
import { SendTransactionsResponse } from '@safe-global/safe-apps-sdk/src/types';
import { TenderlyService } from './tenderly.service';

@Injectable({
  providedIn: 'root',
})
export class BatchTradeService {
  private safeAppsSDK: SafeAppsSDK;

  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private readonly signerService: SignerService,
    private readonly transactionService: TransactionService,
    private readonly rebalancingRepository: RebalancingRepository,
    private readonly chainRepository: ChainRepository,
    private readonly alertService: AlertService,
    private readonly tradeService: TradeService,
    private readonly isLoadingService: IsLoadingService,
    private readonly errorHandler: ErrorHandler,
    private readonly sentryService: SentryService,
    private readonly walletModalService: WalletModalService,
    private readonly transactionExecutionRepository: TransactionExecutionRepository,
    private readonly tenderlyService: TenderlyService,
  ) {
    this.safeAppsSDK = new SafeAppsSDK({
      allowedDomains: [/app\.safe\.global/],
      // debug: true,
    });
  }

  public isBatchTradeSupported(): boolean {
    const chain = this.chainRepository.store.getValue().chain;
    return chain !== undefined && chain.batchTradeSupported;
  }

  public async executeRebalancing(rebalancing: Rebalancing): Promise<void> {
    rebalancing.trades.forEach(trade => {
      this.rebalancingRepository.updateTrade(trade.id, {
        tradeState: TradeSateEnum.RUNNING,
      });
    });
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.USER_SIGN,
    );

    let tx: TransactionInput;

    try {
      if (this.walletModalService.getIsSafe()) {
        tx = await this.executeWithSafe(rebalancing);
      } else {
        tx = await this.executeWithSigner(rebalancing);
      }
    } catch (e) {
      if (!this.transactionExecutionRepository.store.getValue().failedStep) {
        this.transactionExecutionRepository.setFailedStep(
          TransactionExecutionStep.USER_SIGN,
        );
      }
      rebalancing.trades.forEach(trade => {
        if (trade.tradeState === TradeSateEnum.RUNNING) {
          trade.tradeState = TradeSateEnum.READY;
        }
      });
      this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
      throw new Error(`Couldn't execute rebalancing`);
    }

    this.rebalancingRepository.setTxHash(tx.hash);

    const transaction = new Transaction(
      tx,
      'Batch Trade',
      TransactionType.BATCH_TRADE,
    );
    this.transactionExecutionRepository.setTransaction(transaction);
    await this.transactionService.addTransaction(transaction, rebalancing.id);

    this.setupTransactionObserver(transaction, rebalancing);
  }

  private async executeWithSigner(
    rebalancing: Rebalancing,
  ): Promise<TransactionInput> {
    const signer = this.signerService.getSigner();
    if (!signer) {
      throw new Error('Wallet not connected anymore');
    }

    let gasLimit: bigint;
    try {
      try {
        const tenderlySimulation =
          await this.tenderlyService.simulateRebalancing(rebalancing);

        this.transactionExecutionRepository.setTenderlySimulation(
          tenderlySimulation,
        );

        if (!tenderlySimulation.status) {
          this.sentryService.captureError(
            new Error('Tenderly simulation failed'),
          );
          throw new Error('Tenderly simulation failed');
        }

        gasLimit = BigInt(Math.round(tenderlySimulation.gas * 1.2));
      } catch (e) {
        gasLimit = await this.isLoadingService.add(
          this.estimateGasUnits(rebalancing),
          { key: 'gas-estimation' },
        );
      }
    } catch (e) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.SIMULATE,
      );
      rebalancing.trades.forEach(
        trade => (trade.tradeState = TradeSateEnum.READY),
      );
      this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
      throw new Error(`Tenderly simulation failed. Can't execute rebalancing.`);
    }

    try {
      const tx = await signer.sendTransaction({
        to: rebalancing.txHandler,
        data: rebalancing.txData,
        value: rebalancing.txValue,
        gasLimit,
      });

      return {
        hash: tx.hash,
        to: tx.to || undefined,
        data: tx.data,
      };
    } catch (e) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.USER_SIGN,
      );
      rebalancing.trades.forEach(trade => {
        if (trade.tradeState === TradeSateEnum.RUNNING) {
          trade.tradeState = TradeSateEnum.READY;
        }
      });
      this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
      throw e;
    }
  }

  private async executeWithSafe(
    rebalancing: Rebalancing,
  ): Promise<TransactionInput> {
    const transactions: { to: Address; value: string; data: string }[] = [];
    rebalancing.allowances.forEach(allowance => {
      // if token (e.g. USDT) needs reset, set allowance to 0, before setting actual allowance
      if (allowance.resetNeeded) {
        transactions.push({
          to: allowance.token.address,
          value: '0',
          data: ERC20__factory.createInterface().encodeFunctionData('approve', [
            allowance.target,
            0n,
          ]),
        });
      }
      transactions.push({
        to: allowance.token.address,
        value: '0',
        data: ERC20__factory.createInterface().encodeFunctionData('approve', [
          allowance.target,
          allowance.allowanceNeeded,
        ]),
      });
    });

    transactions.push({
      to: rebalancing.txHandler!,
      value: rebalancing.txValue?.toString() || '0',
      data: rebalancing.txData!,
    });

    let response: SendTransactionsResponse;
    try {
      response = await this.safeAppsSDK.txs.send({ txs: transactions });
    } catch (e) {
      throw new Error(`Couldn't execute rebalancing with safe: ${e}`);
    }

    const safeTxHash = response.safeTxHash;
    while (true) {
      try {
        const txDetails =
          await this.safeAppsSDK.txs.getBySafeTxHash(safeTxHash);

        switch (txDetails.txStatus) {
          case TransactionStatus.SUCCESS:
            if (!txDetails.txHash)
              throw new Error('Tx succeeded but missing on-chain hash');
            return {
              hash: txDetails.txHash,
              to: txDetails.txData?.to.value || undefined,
              data: txDetails.txData?.hexData || '',
            };
          case TransactionStatus.FAILED:
            throw new Error(
              `Safe tx failed. safeTxHash=${safeTxHash}, txHash=${txDetails.txHash ?? 'N/A'}`,
            );
          case TransactionStatus.CANCELLED:
            throw new Error(`Safe tx was cancelled. safeTxHash=${safeTxHash}`);
          default:
            // Still waiting (AWAITING_CONFIRMATIONS / AWAITING_EXECUTION / PENDING)
            await sleep(5000);
        }
      } catch (e) {
        // silent fail
      }
    }
  }

  private async estimateGasUnits(rebalancing: Rebalancing): Promise<bigint> {
    this.transactionExecutionRepository.setStep(
      TransactionExecutionStep.SIMULATE,
    );

    const signer = this.signerService.getSigner();
    if (!signer) {
      throw new Error('Wallet not connected anymore');
    }

    try {
      const estimate = await signer.estimateGas({
        to: rebalancing.txHandler,
        data: rebalancing.txData,
        value: rebalancing.txValue,
      });

      return Promise.resolve((estimate * 120n) / 100n); // 20% Buffer
    } catch (e: any) {
      this.transactionExecutionRepository.setFailedStep(
        TransactionExecutionStep.SIMULATE,
      );
      this.sentryService.captureError(e as Error);
      this.handleEstimateGasError(e, rebalancing);
      throw e;
    }
  }

  private handleEstimateGasError(e: any, rebalancing: Rebalancing): void {
    this.alertService.showError(
      'rebalancing.error.estimationError',
      undefined,
      false,
      -1,
    );

    let decodable = false;
    try {
      let revertData = e.error?.data?.data?.data;
      if (!revertData) {
        revertData = e.error?.data?.originalError?.data;
      }

      if (revertData) {
        const decodedError =
          BatchTrade__factory.createInterface().parseError(revertData);

        this.errorHandler.handleError(
          `Couldn't execute rebalancing with id ${rebalancing.id} due to: ${decodedError}`,
        );
        this.sentryService.captureMsg(
          `Couldn't execute rebalancing with id ${rebalancing.id} due to: ${decodedError}`,
        );
        if (!this.appConfig.production) {
          console.error(decodedError);
        }

        if (decodedError?.args['index']) {
          const tradeIndex = +decodedError.args['index'].toString();
          this.showAlertForFailingTrade(rebalancing.trades[tradeIndex]);
        }
        decodable = true;
      } else {
        console.error(`Couldn't extract revert data from error`, e);
      }
    } catch (inner) {
      console.error(`Couldn't decode gas estimation error: `, inner);
    }

    // TODO: this doesn't work if the signer doesn't hold the assets (e.g. enzyme)
    if (!decodable) {
      this.tryToFindFailingTradeBySingleGasEstimations(rebalancing);
    }
  }

  private async tryToFindFailingTradeBySingleGasEstimations(
    rebalancing: Rebalancing,
  ) {
    for (const trade of rebalancing.trades) {
      try {
        await this.tradeService.estimateGasUnits(
          this.tradeService.generateTransactionRequest(trade),
        );
      } catch (error) {
        this.errorHandler.handleError(
          `Couldn't execute rebalancing with id ${rebalancing.id} due to: ${error}`,
        );
        if (!this.appConfig.production) {
          console.error(error);
        }

        if (!(trade.previouslyRequiredTrades?.length > 0)) {
          this.showAlertForFailingTrade(trade);
        }
        break;
      }
    }
  }

  private showAlertForFailingTrade(trade: Trade): void {
    this.alertService.showError(
      'rebalancing.error.tradeFails',
      {
        tradePairString: trade.toString(),
      },
      false,
      -1,
    );
  }

  private setupTransactionObserver(
    transaction: Transaction,
    rebalancing: Rebalancing,
  ): void {
    transaction.getFinishedObservable().subscribe((finished: boolean) => {
      if (finished) {
        if (transaction.success) {
          // TODO: handle partly finished
          this.transactionExecutionRepository.setStep(
            TransactionExecutionStep.SUCCESS,
          );

          const events = transaction.getEvents(
            BatchTrade__factory.createInterface(),
          );

          let rebalancingState = RebalancingStep.FINISHED;
          if (!events || events.length === 0) {
            rebalancingState = RebalancingStep.APPROVAL_OK;
            this.alertService.showError('rebalancing.error.unexpectedTxError');
            this.errorHandler.handleError(
              `No events for executed rebalancing tx#${transaction.hash}`,
            );
            this.sentryService.captureMsg(
              `No events for executed rebalancing tx#${transaction.hash}`,
            );
          }

          const relevantEvents = events?.filter(
            event =>
              event.name === 'TradeExecuted' || event.name === 'TradeFailed',
          );
          rebalancing.trades.forEach((trade, index) => {
            if (
              relevantEvents &&
              relevantEvents[index] &&
              relevantEvents[index].name === 'TradeExecuted'
            ) {
              trade.tradeState = TradeSateEnum.SUCCESS;
            } else if (
              relevantEvents &&
              relevantEvents[index] &&
              relevantEvents[index].name === 'TradeFailedReason'
            ) {
              try {
                this.tradeService.handleTransactionError(
                  {
                    message: EthErrorUtil.decodeReasonFromLogDescription(
                      relevantEvents[index],
                    ),
                  },
                  trade,
                  transaction.hash,
                );
              } catch (e) {
                console.log('Error decoding transaction error');
                if (!this.appConfig.production) {
                  console.error(e);
                }
              }
              trade.tradeState = TradeSateEnum.FAILED;
              if (index === 0) {
                rebalancingState = RebalancingStep.APPROVAL_OK;
              } else {
                rebalancingState = RebalancingStep.PARTLY_FINISHED;
                this.transactionExecutionRepository.setStep(
                  TransactionExecutionStep.SUCCESS_PARTLY,
                );
              }
            } else {
              trade.tradeState = TradeSateEnum.READY;
            }
          });
          this.rebalancingRepository.setStep(rebalancingState);
        } else {
          this.transactionExecutionRepository.setStep(
            TransactionExecutionStep.ERROR,
          );

          const chain = this.chainRepository.store.getValue().chain;
          this.alertService.showError('rebalancing.error.unexpectedTxError', {
            txLink: transaction.hash
              ? BLOCK_EXPLORER_TX_LINK(transaction.hash, chain)
              : undefined,
            tenderlyLink: transaction.hash
              ? `https://dashboard.tenderly.co/tx/${chain?.getIdentifierAsDecimal()}/${
                  transaction.hash
                }`
              : undefined,
          });

          this.alertService.showError('rebalancing.error.unexpectedTxError');
          this.errorHandler.handleError(
            `Rebalancing tx#${transaction.hash} failed`,
          );
          this.sentryService.captureMsg(
            `Rebalancing tx#${transaction.hash} failed`,
          );
          rebalancing.trades.forEach(
            trade => (trade.tradeState = TradeSateEnum.FAILED),
          );
          this.rebalancingRepository.setStep(RebalancingStep.APPROVAL_OK);
        }
        this.transactionService.finishTransaction(transaction);
      }
    });
  }
}
